// src/index.ts
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// src/utils.ts
function parseRomanNumber(roman) {
  roman = roman.toUpperCase();
  let value = 0;
  const values = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1e3 };
  let i = roman.length;
  let lastVal = 0;
  while (i--) {
    if (values[roman.charAt(i)] >= lastVal) {
      value += values[roman.charAt(i)];
    } else {
      value -= values[roman.charAt(i)];
    }
    lastVal = values[roman.charAt(i)];
  }
  return value;
}
function parseLetterNumber(str) {
  const alphaVal = (s) => s.toLowerCase().charCodeAt(0) - 97 + 1;
  let value = 0;
  let i = str.length;
  while (i--) {
    const factor = Math.pow(26, str.length - i - 1);
    value += alphaVal(str.charAt(i)) * factor;
  }
  return value;
}
function unwrapNode(node) {
  const parent = node.parentNode;
  while (node.firstChild) {
    parent == null ? void 0 : parent.insertBefore(node.firstChild, node);
  }
  parent == null ? void 0 : parent.removeChild(node);
}
function parseStyleAttribute(el) {
  var _a;
  const styleRaw = ((_a = el == null ? void 0 : el.attributes[`style`]) == null ? void 0 : _a.value) || ``;
  return Object.fromEntries(styleRaw.split(`;`).map((line) => line.split(`:`).map((v) => v.trim())));
}

// src/transform/style.ts
function transformMsoStyles(html) {
  html = html.replace(/<o:p>(.*?)<\/o:p>/g, ``);
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, `text/html`);
  doc.querySelectorAll(`[style*="mso-"]`).forEach((node) => {
    const styles = parseStyleAttribute(node);
    const newStyles = [];
    for (const prop of Object.keys(styles)) {
      if (prop && !prop.startsWith(`mso-`)) {
        newStyles.push(`${prop}: ${styles[prop]}`);
      }
    }
    node.setAttribute(`style`, newStyles.join(`;`));
  });
  doc.querySelectorAll(`[style*="color: black"]`).forEach((node) => {
    node.style.removeProperty(`color`);
  });
  return doc.documentElement.outerHTML;
}

// src/transform/bookmark.ts
function transformRemoveBookmarks(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, `text/html`);
  const bookmarks = doc.querySelectorAll(`[style*="mso-bookmark:"]`);
  bookmarks.forEach((node) => {
    const bookmark = parseStyleAttribute(node)[`mso-bookmark`];
    const bookmarkLink = doc.querySelector(`a[name="${bookmark}"]`);
    if (bookmarkLink) {
      unwrapNode(bookmarkLink);
    }
    unwrapNode(node);
  });
  return doc.documentElement.outerHTML;
}

// src/transform/list.ts
function transformLists(html) {
  if (html.indexOf(`mso-list:`) === -1) {
    return html;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, `text/html`);
  let listStack = [];
  let currentListId;
  const listElements = doc.querySelectorAll(`p[style*="mso-list:"]`);
  listElements.forEach((node) => {
    const el = node;
    const [msoListId, msoListLevel] = parseMsoListAttribute(parseStyleAttribute(el)[`mso-list`]);
    if (currentListId !== msoListId && (hasNonListItemSibling(el) || msoListLevel === 1)) {
      currentListId = msoListId;
      listStack = [];
    }
    while (msoListLevel > listStack.length) {
      const newList = createListElement(el);
      if (listStack.length > 0) {
        listStack[listStack.length - 1].appendChild(newList);
      } else {
        el.before(newList);
      }
      listStack.push(newList);
    }
    while (msoListLevel < listStack.length) {
      listStack.pop();
    }
    listStack[listStack.length - 1].appendChild(getListItemFromParagraph(el));
    el.remove();
  });
  return doc.documentElement.outerHTML;
}
function hasNonListItemSibling(el) {
  return !el.previousElementSibling || !(el.previousElementSibling.nodeName === `OL` || el.previousElementSibling.nodeName === `UL`);
}
function getListItemFromParagraph(el) {
  const li = document.createElement(`li`);
  li.innerHTML = el.innerHTML.replace(listTypeRegex, ``);
  return li;
}
function parseMsoListAttribute(attr) {
  var _a;
  const msoListValue = attr;
  const msoListInfos = msoListValue.split(` `);
  const msoListId = msoListInfos.find((e) => /l[0-9]+/.test(e)) || ``;
  const msoListLevel = +(((_a = msoListInfos.find((e) => e.startsWith(`level`))) == null ? void 0 : _a.substring(5)) || 1);
  return [msoListId, msoListLevel];
}
var listTypeRegex = /<!--\[if \!supportLists\]-->((.|\n)*)<!--\[endif\]-->/m;
function getListPrefix(el) {
  var _a;
  const matches = el.innerHTML.match(listTypeRegex);
  if (matches == null ? void 0 : matches.length) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(matches[0], `text/html`);
    return ((_a = doc.body.querySelector(`span`)) == null ? void 0 : _a.textContent) || ``;
  }
  return ``;
}
function createListElement(el) {
  const listInfo = getListInfo(getListPrefix(el));
  const list = document.createElement(listInfo.type);
  if (listInfo.countType) {
    list.setAttribute(`type`, listInfo.countType);
  }
  if (listInfo.start > 1) {
    list.setAttribute(`start`, listInfo.start.toString());
  }
  return list;
}
var listOrderRegex = {
  number: /[0-9]+\./,
  romanLower: /(?=[mdclxvi])m*(c[md]|d?c*)(x[cl]|l?x*)(i[xv]|v?i*)\./,
  romanUpper: /(?=[MDCLXVI])M*(C[MD]|D?C*)(X[CL]|L?X*)(I[XV]|V?I*)\./,
  letterLower: /[a-z]+\./,
  letterUpper: /[A-Z]+\./
};
function getListInfo(prefix) {
  let type = `ul`;
  let countType = null;
  let start = 1;
  let matches;
  if (matches = prefix.match(listOrderRegex.number)) {
    type = `ol`;
    start = +matches[0].replace(`.`, ``);
  } else if (matches = prefix.match(listOrderRegex.romanLower)) {
    type = `ol`;
    countType = `i`;
    start = +parseRomanNumber(matches[0].replace(`.`, ``));
  } else if (matches = prefix.match(listOrderRegex.romanUpper)) {
    type = `ol`;
    countType = `I`;
    start = +parseRomanNumber(matches[0].replace(`.`, ``));
  } else if (matches = prefix.match(listOrderRegex.letterLower)) {
    type = `ol`;
    countType = `a`;
    start = +parseLetterNumber(matches[0].replace(`.`, ``));
  } else if (matches = prefix.match(listOrderRegex.letterUpper)) {
    type = `ol`;
    countType = `A`;
    start = +parseLetterNumber(matches[0].replace(`.`, ``));
  }
  return {
    type,
    start,
    countType
  };
}

// src/transform/line-number.ts
function transformRemoveLineNumberWrapper(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, `text/html`);
  const lineNumbers = doc.querySelectorAll(`[class*="MsoLineNumber"]`);
  lineNumbers.forEach((node) => {
    unwrapNode(node);
  });
  return doc.documentElement.outerHTML;
}

// src/transform/html-classes.ts
function transformMsoHtmlClasses(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, `text/html`);
  doc.querySelectorAll(`p[class*="MsoNormal"]`).forEach((node) => {
    node.classList.remove(`MsoNormal`);
  });
  return doc.documentElement.outerHTML;
}

// src/index.ts
var OfficePaste = Extension.create({
  priority: 99999,
  name: `office-paste`,
  addProseMirrorPlugins() {
    return [OfficePastePlugin];
  }
});
var OfficePastePlugin = new Plugin({
  key: new PluginKey("office-paste"),
  props: {
    transformPastedHTML(html) {
      if (html.indexOf(`microsoft-com`) !== -1 && html.indexOf(`office`) !== -1) {
        html = transformLists(html);
        html = transformRemoveBookmarks(html);
        html = transformMsoStyles(html);
        html = transformMsoHtmlClasses(html);
        html = transformRemoveLineNumberWrapper(html);
      }
      return html;
    }
  }
});
var index_default = OfficePaste;
export {
  index_default as default
};
